import tid from '@/common/infrastructure/config/tid';
import { FormikTextField } from '@/common/presentation/components/forms/fields/FormikTextField';
import { JobSiteModalFormData, JobSiteModalValidationSchema } from '@/jobSites/presentation/JobSiteValidationSchema';
import { Button, Dialog, DialogContent, DialogTitle, Grid2 } from '@mui/material';
import { Form, Formik, FormikHelpers } from 'formik';
import AddIcon from '@mui/icons-material/Add';
import { useCreateJobSite } from '@/jobSites/application/adapters/createJobSite';
import { useSnackbar } from '@/common/presentation/SnackbarProvider';
import { FormikSubmitFormButton } from '@/common/presentation/components/forms/FormikSubmitFormButton/FormikSubmitFormButton';
import { JobSiteAutocompleteOption } from '@/jobs/application/adapters/listJobSitesAutocomplete';
import LocationForm from '@/map/presentation/LocationForm';
import { formatPhoneNumber } from '@/common/application/utils/phoneUtils';

const initialValues: JobSiteModalFormData = {
  jobSiteName: '',
  jobPhone: '',
  specialInstructions: '',
  address: '',
  city: '',
  state: '',
  country: '',
  zipCode: '',
  latitude: undefined,
  longitude: undefined
};

export interface JobSiteCreateModalProps {
  isOpen: boolean;
  toggleModal: () => void;
  customerName: string;
  customerId: number;
  setJobSiteFieldValue: (jobSiteId?: JobSiteAutocompleteOption['value']) => void;
  refetchJobSites?: () => Promise<unknown>;
  onJobSiteCreated?: (jobSiteData: {
    id: number | null;
    jobSiteName: string | null;
    address: string | null;
    latitude?: number | null;
    longitude?: number | null;
  }) => void | Promise<void>;
}

export const JobSiteCreateModal = ({
  isOpen,
  toggleModal,
  customerName,
  customerId,
  setJobSiteFieldValue,
  refetchJobSites,
  onJobSiteCreated
}: JobSiteCreateModalProps) => {
  const { showSnackbar, showErrorWithDetails } = useSnackbar();
  const { handleCreateJobSite, loading } = useCreateJobSite();

  const handleModalSubmit = async (values: JobSiteModalFormData, { setSubmitting }: FormikHelpers<JobSiteModalFormData>) => {
    try {
      const { data, isSuccess } = await handleCreateJobSite({
        customerId,
        jobSiteName: values.jobSiteName,
        jobPhone: values.jobPhone?.trim() || undefined,
        specialInstructions: values.specialInstructions,
        address: values.address,
        state: values.state,
        city: values.city,
        latitude: values.latitude,
        longitude: values.longitude,
        powerlinePresent: false
      });
      if (!isSuccess) throw new Error('Job site creation failed');

      await refetchJobSites?.();
      setJobSiteFieldValue(data?.id || undefined);

      // Call the callback to populate timezone if provided
      if (onJobSiteCreated && data) {
        await onJobSiteCreated({
          id: data.id,
          jobSiteName: data.jobSiteName,
          address: data.address,
          latitude: data.latitude,
          longitude: data.longitude
        });
      }

      showSnackbar('Success', 'JobSite created successfully', 'success');
      toggleModal();
    } catch (error: unknown) {
      showErrorWithDetails(error, 'Failed to create JobSite', 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={toggleModal}
      maxWidth="sm"
      fullWidth
      sx={{
        zIndex: 1400 // Higher z-index to appear above parent job creation modal
      }}
    >
      <DialogTitle>Create New JobSite for {customerName}</DialogTitle>
      <DialogContent>
        <Formik
          enableReinitialize
          validateOnChange
          initialValues={initialValues}
          validationSchema={JobSiteModalValidationSchema}
          onSubmit={handleModalSubmit}
        >
          {({ isSubmitting }) => (
            <Form>
              {/* Job Site Form Fields */}
              <Grid2 container spacing={2} sx={{ mt: 1 }}>
                <Grid2 size={12}>
                  <FormikTextField
                    id="jobSiteName"
                    label="Job Site Name"
                    type="text"
                    placeholder="Enter Jobsite Name"
                    {...tid('jobs:dispatch:jobsite-modal:name-input')}
                  />
                </Grid2>

                <Grid2 size={12}>
                  <FormikTextField
                    id="jobPhone"
                    label="Job Site Phone"
                    placeholder="(XXX) XXX-XXXX"
                    transformOnChange={formatPhoneNumber}
                    testId="jobs:dispatch:jobsite-modal:job-phone-input"
                    slotProps={{
                      htmlInput: {
                        inputMode: 'tel',
                        maxLength: 14
                      }
                    }}
                  />
                </Grid2>

                <Grid2 size={12}>
                  <FormikTextField
                    id="specialInstructions"
                    label="Special Instructions"
                    type="text"
                    multiline
                    rows={3}
                    testId="jobs:dispatch:jobsite-modal:special-instructions-input"
                  />
                </Grid2>

                {/* Location Form */}
                <Grid2 size={12}>
                  <LocationForm hideCoordinates={true} />
                </Grid2>
              </Grid2>

              {/* Action Buttons */}
              <Grid2 container sx={{ mt: 3 }}>
                <Grid2 size={12} display="flex" justifyContent="flex-end" alignItems="center">
                  <Button
                    variant="text"
                    onClick={toggleModal}
                    sx={{ textTransform: 'none', fontWeight: 500, marginRight: 2 }}
                    {...tid('jobs:dispatch:jobsite-modal:cancel-button')}
                  >
                    CANCEL
                  </Button>

                  <FormikSubmitFormButton
                    isHotkeyEnabled={isOpen}
                    testId="jobs:dispatch:jobsite-modal:save-button"
                    variant="contained"
                    sx={{ paddingX: 3 }}
                    startIcon={<AddIcon />}
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="job-sites:management:save-button" // Additional testid for E2E compatibility
                  >
                    {isSubmitting || loading ? 'Saving...' : 'CREATE JOBSITE'}
                  </FormikSubmitFormButton>
                </Grid2>
              </Grid2>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};
